import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { StatCard } from "../components/dashboard/StatCard";
import { AlertCard } from "../components/dashboard/AlertCard";
import { DishPerformanceTable } from "../components/dashboard/DishPerformanceTable";
import { RevenueChart } from "../components/dashboard/RevenueChart";
import { CostBreakdownChart } from "../components/dashboard/CostBreakdownChart";
import {
  IndianRupee,
  TrendingUp,
  Utensils,
  Percent,
  ShoppingBag,
  AlertTriangle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { handleError } from "../CommonJquery/CommonJquery";
import {
  get_dashboard_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection";

export default function Dashboard() {
  const [filterType, setFilterType] = useState("all");
  const restroName = retrieveData("restaurant_name");
  const [dashboard, setDashboard] = useState({
    dashboard_cards: {},
    alerts: [],
    topProfitableDishes: [],
    lossMakingDishes: [],
    data_revenue: [],
    data_dish_performance: [],
  });

  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);

  useEffect(() => {
    master_data_get(filterType);
  }, [filterType]);

  const master_data_get = async (
    filter_type = "all",
    from_date = "",
    to_date = "",
  ) => {
    setshowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("filter_type", filter_type);

    if (filter_type === "custom") {
      fd.append("from_date", from_date);
      fd.append("to_date", to_date);
    }

    await server_post_data(get_dashboard_data, fd)
      .then((Response) => {
        if (Response.data.error) {
          handleError(Response.data.message);
        } else {
          setDashboard(Response.data.message);
        }
        setshowLoaderAdmin(false);
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  return (
    <MainLayout>
      <Header
        title="Financial Intelligence"
        subtitle={`${restroName} - Asian Restaurant & Bar`}
        filterType={filterType}
        onFilterChange={setFilterType}
      />

      <div className="p-6 space-y-6">
        {/* Top Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Today's Revenue"
            value={`₹${dashboard.dashboard_cards?.revenue?.value || 0}`}
            change={dashboard.dashboard_cards?.revenue?.change || 0}
            trend={dashboard.dashboard_cards?.revenue?.trend || "up"}
            icon={<IndianRupee className="w-6 h-6" />}
            accentColor="primary"
          />

          <StatCard
            title="Food Cost"
            value={`₹${dashboard.dashboard_cards?.food_cost?.value || 0}`}
            change={dashboard.dashboard_cards?.food_cost?.change || 0}
            trend={dashboard.dashboard_cards?.food_cost?.trend || "down"}
            changeLabel="vs yesterday (good)"
            icon={<ShoppingBag className="w-6 h-6" />}
            accentColor="secondary"
          />

          <StatCard
            title="Gross Profit"
            value={`₹${dashboard.dashboard_cards?.gross_profit?.value || 0}`}
            change={dashboard.dashboard_cards?.gross_profit?.change || 0}
            trend={dashboard.dashboard_cards?.gross_profit?.trend || "up"}
            icon={<TrendingUp className="w-6 h-6" />}
            accentColor="success"
          />

          <StatCard
            title="Food Cost %"
            value={`${
              dashboard.dashboard_cards?.food_cost_percent?.value || 0
            }%`}
            change={dashboard.dashboard_cards?.food_cost_percent?.change || 0}
            trend={
              dashboard.dashboard_cards?.food_cost_percent?.trend || "down"
            }
            changeLabel="Target: 32%"
            icon={<Percent className="w-6 h-6" />}
            accentColor="warning"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Dishes Sold"
            value={dashboard.dashboard_cards?.dishes_sold?.value || 0}
            change={dashboard.dashboard_cards?.dishes_sold?.change || 0}
            trend={dashboard.dashboard_cards?.dishes_sold?.trend || "up"}
            icon={<Utensils className="w-6 h-6" />}
            accentColor="primary"
          />

          <StatCard
            title="Avg Dish Profit"
            value={`₹${dashboard.dashboard_cards?.avg_dish_profit?.value || 0}`}
            change={dashboard.dashboard_cards?.avg_dish_profit?.change || 0}
            trend={dashboard.dashboard_cards?.avg_dish_profit?.trend || "up"}
            icon={<IndianRupee className="w-6 h-6" />}
            accentColor="success"
          />

          <StatCard
            title="Total orders"
            value={`${dashboard.dashboard_cards?.total_orders?.value || 0}`}
            change={dashboard.dashboard_cards?.total_orders?.change || 0}
            trend={dashboard.dashboard_cards?.total_orders?.trend || "down"}
            icon={<Percent className="w-6 h-6" />}
            accentColor="secondary"
          />
        </div>

        {/* Alerts Section */}

        {dashboard.alerts && dashboard.alerts.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Alerts & Actions
            </h2>
            <div className="grid gap-3">
              {dashboard.alerts.map((a, i) => (
                <AlertCard
                  key={i}
                  type={a.type}
                  title={a.title}
                  description={a.description}
                  action={a.action?.label}
                  actionUrl={a.action?.url}
                />
              ))}
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RevenueChart data_revenue={dashboard.data_revenue} />
          </div>
          <CostBreakdownChart
            data_dish_performance={dashboard.data_dish_performance}
          />
        </div>

        {/* Dish Performance Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DishPerformanceTable
            type="profitable"
            topProfitableDishes={dashboard.topProfitableDishes}
            lossMakingDishes={dashboard.lossMakingDishes}
          />
          <DishPerformanceTable
            type="loss"
            topProfitableDishes={dashboard.topProfitableDishes}
            lossMakingDishes={dashboard.lossMakingDishes}
          />
        </div>
      </div>
    </MainLayout>
  );
}
